import React, { memo, useMemo } from 'react';
import { Box, Paper } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { formatDateRu, isWeekend, weekdayShort } from '../edit/editCalc';
import { SummaryField, SummaryRow } from './summaryApi';
import { groupSummaryColumns } from './summaryCalc';
import { formatCell } from './summaryExport';

const BORDER = '1px solid #e2e8f0';
const DATE_COL_WIDTH = 140;
const COL_WIDTH = 128;
const GROUP_ROW_HEIGHT = 34;

/**
 * Оформление повторяет месячную сетку «Ред. мес. отчет» (edit/MonthGrid.tsx): двухуровневая шапка с группами,
 * выходные оттенены, дата с днем недели. Стили заданы один раз на контейнере вложенными селекторами,
 * а не на каждую ячейку: за год это около 7 000 ячеек, и отдельный sx у каждой заметно замедляет отрисовку.
 */
const gridSx: SxProps<Theme> = {
  overflow: 'auto',
  maxHeight: 'calc(100vh - 250px)',
  minHeight: 200,
  '& table': { borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed' },

  '& th': {
    position: 'sticky',
    boxSizing: 'border-box',
    padding: '6px 8px',
    backgroundColor: '#f8fafc',
    color: '#475569',
    fontFamily: 'inherit',
    verticalAlign: 'bottom'
  },
  '& th.corner': {
    top: 0, left: 0, zIndex: 4, textAlign: 'left', fontSize: '0.78rem', fontWeight: 700,
    borderBottom: BORDER, borderRight: BORDER
  },
  '& th.grp': {
    top: 0, zIndex: 3, height: GROUP_ROW_HEIGHT, textAlign: 'left', fontSize: '0.74rem', fontWeight: 700,
    lineHeight: 1.3, textTransform: 'uppercase', letterSpacing: '0.4px',
    borderBottom: '1px solid #f1f5f9', borderLeft: BORDER
  },
  '& th.col': {
    top: GROUP_ROW_HEIGHT, zIndex: 3, textAlign: 'right', fontSize: '0.72rem', fontWeight: 600, lineHeight: 1.25,
    color: '#64748b', borderBottom: BORDER, overflowWrap: 'break-word'
  },
  '& th.col.imp': { color: '#334155', fontWeight: 700 },

  '& tbody tr': { backgroundColor: '#ffffff' },
  '& tbody tr.we': { backgroundColor: '#f8fafc' },
  '& tbody tr:hover': { backgroundColor: '#f1f5f9' },

  '& td': {
    boxSizing: 'border-box',
    padding: '4px 8px',
    borderBottom: '1px solid #f1f5f9',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap'
  },
  '& td.day': {
    position: 'sticky', left: 0, zIndex: 1, backgroundColor: 'inherit', borderRight: BORDER,
    fontSize: '0.84rem', fontWeight: 700, color: '#1e293b', lineHeight: '28px'
  },
  '& td.day span': { marginLeft: '6px', fontWeight: 500, color: '#64748b' },

  '& td.num': {
    textAlign: 'right', fontSize: '0.84rem', fontWeight: 500, lineHeight: '28px',
    color: '#475569', fontVariantNumeric: 'tabular-nums'
  },
  '& td.num.s': { fontWeight: 700, color: '#1e293b' },
  '& td.num.nil': { color: '#cbd5e1' },

  // Итоги закреплены снизу
  '& tfoot td': {
    position: 'sticky', bottom: 0, zIndex: 2, backgroundColor: '#f1f5f9', color: '#0f172a',
    textAlign: 'right', fontSize: '0.84rem', fontWeight: 700, lineHeight: '28px', fontVariantNumeric: 'tabular-nums',
    borderTop: '1px solid #cbd5e1', borderBottom: 'none'
  },
  '& tfoot td.day': { zIndex: 3, textAlign: 'left', backgroundColor: '#f1f5f9', color: '#0f172a' }
};

interface SummaryGridProps {
  fields: SummaryField[];
  rows: SummaryRow[];
  totals: Record<string, number>;
  /** Предел высоты таблицы (по умолчанию - почти весь экран; на странице с несколькими таблицами задается меньше) */
  maxHeight?: number | string;
}

/** Сама таблица: не перерисовывается, пока не сменились данные (приглушение при перезагрузке ее не затрагивает) */
const SummaryGrid = memo(({ fields, rows, totals, maxHeight }: SummaryGridProps) => {
  // Первое поле - дата, она выводится отдельной закрепленной колонкой
  const dateField = fields[0];
  const columns = useMemo(() => fields.slice(1), [fields]);
  const groups = useMemo(() => groupSummaryColumns(columns), [columns]);
  const tableWidth = DATE_COL_WIDTH + columns.length * COL_WIDTH;

  return (
    <Box sx={gridSx} style={maxHeight === undefined ? undefined : { maxHeight }}>
      <table style={{ width: tableWidth, minWidth: '100%' }}>
        <colgroup>
          <col style={{ width: DATE_COL_WIDTH }} />
          {columns.map((f) => <col key={f.name} style={{ width: COL_WIDTH }} />)}
        </colgroup>
        <thead>
          <tr>
            <th rowSpan={2} className="corner">{dateField ? dateField.label : 'Дата'}</th>
            {groups.map((g, i) => (
              <th key={`${g.name}-${i}`} colSpan={g.span} className="grp">{g.name}</th>
            ))}
          </tr>
          <tr>
            {columns.map((f) => (
              <th key={f.name} className={f.important ? 'col imp' : 'col'}>{f.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.date} className={isWeekend(row.date) ? 'we' : undefined}>
              <td className="day">
                {formatDateRu(row.date)}
                <span>{weekdayShort(row.date)}</span>
              </td>
              {columns.map((f) => {
                const value = row[f.name];
                const empty = value === null || value === undefined;
                return (
                  <td key={f.name} className={`num${f.important ? ' s' : ''}${empty ? ' nil' : ''}`}>
                    {formatCell(value)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td className="day">Итого:</td>
            {columns.map((f) => (
              <td key={f.name}>{f.summable && f.name in totals ? formatCell(totals[f.name]) : ''}</td>
            ))}
          </tr>
        </tfoot>
      </table>
    </Box>
  );
});

interface SummaryTableProps extends SummaryGridProps {
  /** Идет перезагрузка: показанные данные ещё прежние */
  stale?: boolean;
}

const SummaryTable: React.FC<SummaryTableProps> = ({ fields, rows, totals, stale, maxHeight }) => (
  <Paper
    elevation={0}
    sx={{ border: BORDER, borderRadius: '12px', overflow: 'hidden', transition: 'opacity 0.15s ease' }}
    style={{ opacity: stale ? 0.55 : 1 }}
  >
    <SummaryGrid fields={fields} rows={rows} totals={totals} maxHeight={maxHeight} />
  </Paper>
);

export default memo(SummaryTable);
